import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { healthsherpa } from './api';
import ClientPicker from './ClientPicker';
import { usePersonColors } from './HealthSherpaPerson';
import { fs } from './hsScale';
import { smsSegments, smsText } from './hsSms';
import { ConsentSendDialog, gateWords, STATE_TONE } from './HealthSherpaConsent';   // 5.40.0
import { transfer } from './hsLock';   // 5.44.0 every way into HealthSherpa takes the "in use" lock
import { PiCaptureResult } from './HsPersonalInfo';   // 5.48.0

// =============================================================================
// HealthSherpa "Start enrollment" (5.39.0, Henry 2026-11-01). One dialog, every field editable.
// Opened from the page's top bar (pick a source: HealthSherpa client / InsureDocs client / blank), from a quote's
// bottom row (prefilled from that quote) and from an Open Enrollment worklist row (that person + their 2026 data).
// - Agent-assisted (default): "Open application in HealthSherpa" = client_apply_url (5.39.1; new tab, rel noopener
//   noreferrer; the agent signs in there) + a smaller "Shop plans first" = shopping_url. Never sends a plan (CMS rule).
// - Self-service: "Text link to client" (Solutions Telecom, as you) is the main action and texts the APPLY link
//   (5.39.1); "Copy link" sits behind "Client is here with me". May send the quote's plan when exactly one is ticked.
// - 5.39.1: anything left blank is left out of the request (never 0 / empty); tobacco and pregnant are — / Yes / No.
// - Consent: a required tick for BOTH flows; the HealthSherpa export's consent status is shown as a hint.
//   5.40.0: OR a valid signed CMS consent ("CMS consent signed <date>, expires <date>"), or "Send CMS consent form"
//   (texted through Documenso; the dialog checks every 15 s while it waits). A revocation blocks BOTH options until a
//   new consent is signed. The server applies the same gate (409).
// The two links live ONLY in this component's state: never stored, never in the address bar, gone when the dialog
// closes. Client data goes to the server in POST bodies only (5.27.4 rule).
// =============================================================================

const detailOf = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => [x.loc && x.loc.slice(-1)[0], x.msg].filter(Boolean).join(': ')).join('; ');
  return e?.message || 'Something went wrong';
};
const en = () => healthsherpa.enroll;
const REL_LABEL = { primary: 'Primary', spouse: 'Spouse', dependent: 'Dependent' };
const blankPerson = (rel) => ({ relationship: rel, first_name: '', last_name: '', email: '', phone: '', dob: '', age: '', sex: '',
  uses_tobacco: null, pregnant: null });
// 5.39.1: yes / no / not asked (null = left out of the request)
const tri = (v) => (v === true ? true : v === false ? false : null);
const triValue = (v) => (v === true ? 'yes' : v === false ? 'no' : '');
const triParse = (v) => (v === 'yes' ? true : v === 'no' ? false : null);

export function buildSessionBody(form, flow, consent, sendPlan, consentMode = 'hs_tick', consentDocumentId = null) {
  const loc = form.location || {};
  const hh = form.household || {};
  return {
    flow,
    plan_year: Number(form.plan_year),
    locale: form.locale || 'en-US',
    person_id: form.person_id ?? null,
    client_id: form.client_id ?? null,
    quote_id: form.quote_id ?? null,
    consent_attested: consent === true,
    consent_mode: consentMode === 'cms' ? 'cms' : 'hs_tick',             // 5.40.0
    consent_document_id: consentMode === 'cms' ? (consentDocumentId ?? null) : null,
    location: { zip: loc.zip || '', fips: loc.fips || '', state: loc.state || '', county_name: loc.county_name || '',
      street: loc.street || '', city: loc.city || '' },
    household: { annual_income: String(hh.annual_income ?? ''), household_size: String(hh.household_size ?? ''),
      touched: hh.touched || [], carried: hh.carried || [] },                       // 5.48.0
    applicants: (form.applicants || []).map((a) => ({
      relationship: a.relationship, first_name: a.first_name || '', last_name: a.last_name || '',
      email: a.relationship === 'primary' ? (a.email || '') : '', phone: a.relationship === 'primary' ? (a.phone || '') : '',
      dob: a.dob || '', age: a.dob ? '' : String(a.age ?? ''), sex: a.sex || '',
      uses_tobacco: tri(a.uses_tobacco), pregnant: tri(a.pregnant),
      member_key: a.member_key || null, touched: a.touched || [], carried: a.carried || [] })),   // 5.48.0
    plan_id: flow === 'self_service' && sendPlan && form.plan_id ? form.plan_id : null,
  };
}

const sourceLabel = (form) => {
  const k = form?.source?.kind;
  if (k === 'person') return 'a HealthSherpa client';
  if (k === 'client') return 'an InsureDocs client';
  if (k === 'quote') return 'the quote';
  return 'a blank form';
};

function useStyles(c) {
  return useMemo(() => ({
    btn: (primary, disabled) => ({ padding: '7px 14px', fontSize: fs(13), borderRadius: 5, cursor: disabled ? 'default' : 'pointer',
      fontWeight: primary ? 600 : 400, opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap',
      border: primary ? '1px solid #185FA5' : `1px solid ${c.border}`, background: primary ? '#185FA5' : c.panel, color: primary ? '#fff' : c.text }),
    input: (w) => ({ padding: '6px 8px', fontSize: fs(13), borderRadius: 5, border: `1px solid ${c.border}`, background: c.panel, color: c.text,
      width: w || '100%', boxSizing: 'border-box' }),
    label: { fontSize: fs(11), fontWeight: 600, color: c.sub, marginBottom: 3, display: 'block' },
    box: { border: `1px solid ${c.border}`, borderRadius: 8, padding: 12, background: c.panel },
    h: { fontSize: fs(12), fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: c.sub, margin: '0 0 8px' },
  }), [c]);
}

function Err({ msg }) {
  if (!msg) return null;
  return <div role="alert" data-testid="en-error" style={{ background: '#A32D2D', color: '#fff', padding: '8px 12px', borderRadius: 5, fontSize: fs(13), margin: '8px 0' }}>{msg}</div>;
}

// --- source pickers -------------------------------------------------------------------------------------------------
function PersonPicker({ c, s, onPick }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setRows([]); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      setBusy(true);
      // 5.27.4: the search text goes in a POST body
      healthsherpa.policy.people({ q: q.trim(), per_page: 8 }).then((r) => { if (alive) setRows(r.data.rows || []); })
        .catch(() => { if (alive) setRows([]); }).finally(() => { if (alive) setBusy(false); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);
  return (
    <div>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search HealthSherpa clients (name, phone, email…)" style={s.input()} aria-label="Search HealthSherpa clients" data-testid="en-person-search" />
      <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 6 }}>
        {busy && <div style={{ fontSize: fs(12), color: c.sub }}>Searching…</div>}
        {rows.map((r) => (
          <button key={r.person_id} onClick={() => onPick(r.person_id)} data-testid="en-person-row"
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 8px', border: 'none', borderBottom: `1px solid ${c.border}`, background: 'transparent', color: c.text, cursor: 'pointer', fontSize: fs(13) }}>
            <strong>{r.display_name || [r.first_name, r.last_name].filter(Boolean).join(' ') || `Household #${r.person_id}`}</strong>
            {r.client && <span style={{ color: c.sub }}> · {r.client.full_name}</span>}
            {(r.city || r.zip) && <span style={{ color: c.sub }}> · {[r.city, r.zip].filter(Boolean).join(' ')}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function SourceChooser({ c, s, meta, onPick }) {
  const [mode, setMode] = useState(meta?.can_pick_people ? 'person' : 'client');
  return (
    <div style={s.box} data-testid="en-source">
      <div style={s.h}>Prefill from</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        {meta?.can_pick_people && <button style={s.btn(mode === 'person')} onClick={() => setMode('person')}>HealthSherpa client</button>}
        <button style={s.btn(mode === 'client')} onClick={() => setMode('client')}>InsureDocs client</button>
        <button style={s.btn(false)} onClick={() => onPick('blank', null)} data-testid="en-blank">Blank (walk-in)</button>
      </div>
      {mode === 'person' && meta?.can_pick_people && <PersonPicker c={c} s={s} onPick={(id) => onPick('person', id)} />}
      {mode === 'client' && <ClientPicker darkMode={c.darkMode} onPick={(r) => onPick('client', r.id)} />}
    </div>
  );
}

// --- the form ---------------------------------------------------------------------------------------------------------
function PeopleTable({ c, s, form, setForm, agent }) {
  // 5.48.0: personal fields the user changes are "touched" (saved to the client's personal info after the links are issued)
  const PI_KEYS = ['dob', 'sex', 'uses_tobacco'];
  const set = (i, patch) => setForm((f) => ({ ...f, applicants: f.applicants.map((a, j) => {
    if (j !== i) return a;
    const ks = Object.keys(patch).filter((k) => PI_KEYS.includes(k));
    return { ...a, ...patch, touched: Array.from(new Set([...(a.touched || []), ...ks])), carried: (a.carried || []).filter((k) => !ks.includes(k)) };
  }) }));
  const remove = (i) => setForm((f) => ({ ...f, applicants: f.applicants.filter((_a, j) => j !== i) }));
  const add = (rel) => setForm((f) => ({ ...f, applicants: [...f.applicants, blankPerson(rel)] }));
  const hasSpouse = form.applicants.some((a) => a.relationship === 'spouse');
  const td = { padding: '4px 4px', verticalAlign: 'top' };
  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: fs(12) }} data-testid="en-people">
          <thead>
            <tr style={{ color: c.sub, textAlign: 'left' }}>
              <th style={td}>Who</th><th style={td}>First name</th><th style={td}>Last name</th><th style={td}>Date of birth</th>
              <th style={td}>or Age</th><th style={td}>Sex</th><th style={td}>Tobacco</th><th style={td}>Pregnant</th><th style={td} />
            </tr>
          </thead>
          <tbody>
            {form.applicants.map((a, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={i}>
                <tr>
                  <td style={td}><strong>{REL_LABEL[a.relationship] || a.relationship}</strong></td>
                  <td style={td}><input aria-label={`Person ${i + 1} first name`} style={s.input(120)} value={a.first_name} onChange={(e) => set(i, { first_name: e.target.value })} maxLength={50} /></td>
                  <td style={td}><input aria-label={`Person ${i + 1} last name`} style={s.input(120)} value={a.last_name} onChange={(e) => set(i, { last_name: e.target.value })} maxLength={50} /></td>
                  <td style={td}><input aria-label={`Person ${i + 1} date of birth`} type="date" style={s.input(140)} value={a.dob} onChange={(e) => set(i, { dob: e.target.value })} /></td>
                  <td style={td}><input aria-label={`Person ${i + 1} age`} style={s.input(56)} value={a.dob ? '' : a.age} disabled={!!a.dob} onChange={(e) => set(i, { age: e.target.value.replace(/\D/g, '').slice(0, 3) })} /></td>
                  <td style={td}>
                    <select aria-label={`Person ${i + 1} sex`} style={s.input(88)} value={a.sex} onChange={(e) => set(i, { sex: e.target.value })}>
                      <option value="">—</option><option value="female">Female</option><option value="male">Male</option>
                    </select>
                  </td>
                  <td style={td}>
                    <select aria-label={`Person ${i + 1} uses tobacco`} style={s.input(70)} value={triValue(a.uses_tobacco)} onChange={(e) => set(i, { uses_tobacco: triParse(e.target.value) })}>
                      <option value="">—</option><option value="yes">Yes</option><option value="no">No</option>
                    </select>
                    {(a.carried || []).includes('uses_tobacco') && <div style={{ fontSize: fs(10), color: c.sub }} data-testid="en-tobacco-last">last year's</div>}
                  </td>
                  <td style={td}>
                    <select aria-label={`Person ${i + 1} pregnant`} style={s.input(70)} value={triValue(a.pregnant)} onChange={(e) => set(i, { pregnant: triParse(e.target.value) })}>
                      <option value="">—</option><option value="yes">Yes</option><option value="no">No</option>
                    </select>
                  </td>
                  <td style={td}>{a.relationship !== 'primary' && <button style={{ ...s.btn(false), padding: '3px 8px' }} onClick={() => remove(i)} aria-label={`Remove person ${i + 1}`}>✕</button>}</td>
                </tr>
                {a.relationship === 'primary' && (
                  <tr>
                    <td style={td} />
                    <td style={td} colSpan={2}><input aria-label="Primary email" placeholder="Email" style={s.input()} value={a.email} onChange={(e) => set(i, { email: e.target.value })} maxLength={50} /></td>
                    <td style={td} colSpan={2}><input aria-label="Primary phone" placeholder="Mobile phone" style={s.input()} value={a.phone} onChange={(e) => set(i, { phone: e.target.value })} maxLength={20} /></td>
                    <td style={td} colSpan={4}><span style={{ fontSize: fs(11), color: c.sub }}>{agent ? 'Names, email and phone prefill HealthSherpa for the agent.' : 'Sent to HealthSherpa too (it may use only the email on self-service links).'}</span></td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        {!hasSpouse && <button style={s.btn(false)} onClick={() => add('spouse')}>+ Spouse</button>}
        <button style={s.btn(false)} onClick={() => add('dependent')}>+ Dependent</button>
      </div>
    </div>
  );
}

function CountyBox({ c, s, form, setForm }) {
  const [counties, setCounties] = useState(null);
  const [err, setErr] = useState(null);
  const loc = form.location;
  const lookup = async () => {
    setErr(null);
    if (!/^\d{5}$/.test(loc.zip || '')) { setErr('Type a 5-digit ZIP first'); return; }
    try {
      const r = await healthsherpa.counties(loc.zip);
      const list = r.data.counties || [];
      setCounties(list);
      if (list.length === 1) setForm((f) => ({ ...f, location: { ...f.location, fips: list[0].fips_code, county_name: list[0].name || '', state: list[0].state || f.location.state } }));
    } catch (e) { setErr(detailOf(e)); }
  };
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <div><label style={s.label} htmlFor="en-zip">ZIP</label>
        <input id="en-zip" style={s.input(90)} value={loc.zip} maxLength={5}
          onChange={(e) => { const z = e.target.value.replace(/\D/g, '').slice(0, 5); setCounties(null); setForm((f) => ({ ...f, location: { ...f.location, zip: z, fips: '', county_name: '' } })); }} />
      </div>
      <div><label style={s.label} htmlFor="en-county">County</label>
        {counties && counties.length > 1 ? (
          <select id="en-county" style={s.input(200)} value={loc.fips} onChange={(e) => {
            const k = counties.find((x) => x.fips_code === e.target.value);
            setForm((f) => ({ ...f, location: { ...f.location, fips: e.target.value, county_name: k ? k.name : '', state: (k && k.state) || f.location.state } }));
          }}>
            <option value="">Pick the county…</option>
            {counties.map((k) => <option key={k.fips_code} value={k.fips_code}>{k.name}</option>)}
          </select>
        ) : (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: fs(13), minWidth: 90 }} data-testid="en-county-name">{loc.county_name || (loc.fips ? loc.fips : '—')}</span>
            <button style={{ ...s.btn(false), padding: '4px 10px' }} onClick={lookup}>Look up county</button>
          </div>
        )}
      </div>
      <div><label style={s.label} htmlFor="en-state">State</label>
        <input id="en-state" style={s.input(56)} value={loc.state} maxLength={2} onChange={(e) => setForm((f) => ({ ...f, location: { ...f.location, state: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') } }))} />
      </div>
      {err && <span style={{ color: '#c0392b', fontSize: fs(12) }}>{err}</span>}
    </div>
  );
}

// --- the result (links in state only) ------------------------------------------------------------------------------------
function Delivery({ c, s, result, form, consent, onClose }) {
  const sess = result.session;
  const link = result.links.client_apply_url;          // 5.39.1: both flows lead with the application
  const shopLink = result.links.shopping_url;
  const [phone, setPhone] = useState(() => (form.applicants.find((a) => a.relationship === 'primary') || {}).phone || '');
  const [sms, setSms] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmCopy, setConfirmCopy] = useState(false);
  const [done, setDone] = useState(null);
  const template = result.template;
  const preview = smsText(template, sess.plan_year, link);
  const segs = smsSegments(preview);
  const off = !consent;

  const opened = () => { en().delivery(sess.id, 'opened').then(() => setDone('Opened in HealthSherpa')).catch(() => {}); };
  const sendText = async () => {
    setBusy(true); setErr(null); setSms(null);
    try { const r = await en().text(sess.id, phone, link); setSms(r.data); } catch (e) { setErr(detailOf(e)); }
    setBusy(false);
  };
  const copy = async () => {
    setErr(null);
    try {
      await navigator.clipboard.writeText(link);
      await en().delivery(sess.id, 'copied');
      setDone('Link copied — paste it for the client while they are with you');
      setConfirmCopy(false);
    } catch (e) { setErr(detailOf(e) || 'Copy failed'); }
  };

  return (
    <div style={{ ...s.box, borderColor: '#2e7d32' }} data-testid="en-result">
      <div style={{ fontSize: fs(14), fontWeight: 700, color: '#2e7d32', marginBottom: 6 }}>
        ✓ HealthSherpa link ready ({sess.flow === 'agent_assisted' ? 'agent-assisted' : 'self-service'}, plan year {sess.plan_year})
      </div>
      <div style={{ fontSize: fs(12), color: c.sub, marginBottom: 10 }}>
        The link is not saved anywhere — it disappears when you close this window.{result.plan_sent ? ' The quote’s plan was sent.' : ''}
      </div>
      {off && <div style={{ fontSize: fs(12), color: '#c0392b', marginBottom: 8 }}>Tick the consent box to use the link.</div>}
      {sess.flow === 'agent_assisted' ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {off ? <span style={s.btn(true, true)} aria-disabled="true">Open application in HealthSherpa ↗</span> : (
            <button type="button" onClick={() => transfer(link, opened)} data-testid="en-open"
              style={{ ...s.btn(true), textDecoration: 'none', display: 'inline-block' }}>Open application in HealthSherpa ↗</button>
          )}
          {off ? <span style={{ ...s.btn(false, true), padding: '4px 10px', fontSize: fs(12) }} aria-disabled="true">Shop plans first ↗</span> : (
            <button type="button" onClick={() => transfer(shopLink, opened)} data-testid="en-shop"
              style={{ ...s.btn(false), padding: '4px 10px', fontSize: fs(12), textDecoration: 'none', display: 'inline-block' }}>Shop plans first ↗</button>
          )}
          <span style={{ fontSize: fs(12), color: c.sub }}>Sign in to HealthSherpa in the new tab if it asks.</span>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div><label style={s.label} htmlFor="en-sms-phone">Client’s mobile</label>
              <input id="en-sms-phone" style={s.input(160)} value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={20} /></div>
            <button style={s.btn(true, off || busy || !phone.trim())} disabled={off || busy || !phone.trim()} onClick={sendText} data-testid="en-text">
              {busy ? 'Sending…' : 'Text link to client'}
            </button>
            <button style={s.btn(false, off)} disabled={off} onClick={() => setConfirmCopy(true)} data-testid="en-copy">Copy link…</button>
          </div>
          <div style={{ fontSize: fs(12), color: c.sub }} data-testid="en-sms-preview">
            Text: “{template.split('<link>')[0].split('<year>').join(String(sess.plan_year))}<em>[link]</em>{template.split('<link>')[1] || ''}”
            {' '}· {segs ? `${segs} segment${segs === 1 ? '' : 's'}` : 'not sendable'}
          </div>
          {confirmCopy && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }} data-testid="en-copy-confirm">
              <span style={{ fontSize: fs(13) }}>Client is here with me</span>
              <button style={s.btn(true)} onClick={copy}>Copy</button>
              <button style={s.btn(false)} onClick={() => setConfirmCopy(false)}>Cancel</button>
            </div>
          )}
          {sms && (
            <div role="status" data-testid="en-sms-result" style={{ fontSize: fs(13), fontWeight: 600, color: sms.ok ? '#2e7d32' : '#c0392b' }}>
              {sms.ok ? `✓ ${sms.message}` : `✗ ${sms.message}`}{sms.segments ? ` (${sms.segments} segment${sms.segments === 1 ? '' : 's'})` : ''}
            </div>
          )}
        </div>
      )}
      {done && <div role="status" style={{ fontSize: fs(13), color: '#2e7d32', marginTop: 8 }}>✓ {done}</div>}
      <Err msg={err} />
      <div style={{ marginTop: 10 }}><button style={s.btn(false)} onClick={onClose}>Done</button></div>
    </div>
  );
}

// --- the dialog -------------------------------------------------------------------------------------------------------
export default function EnrollDialog({ darkMode, source, onClose }) {
  const c = usePersonColors(darkMode);
  const s = useStyles(c);
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(null);
  const [flow, setFlow] = useState('agent_assisted');
  const [consent, setConsent] = useState(false);
  const [sendPlan, setSendPlan] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [hsErr, setHsErr] = useState(null);
  const [result, setResult] = useState(null);
  const [picking, setPicking] = useState(!source);
  const closeRef = useRef(null);
  // 5.40.0 the CMS consent option
  const [gate, setGate] = useState(null);
  const [mode, setMode] = useState('hs_tick');
  const [walkInConsent, setWalkInConsent] = useState(null);
  const [sendConsent, setSendConsent] = useState(false);
  const [checkMsg, setCheckMsg] = useState(null);       // 5.40.2: what "Check now" found (never nothing)
  const [checking, setChecking] = useState(false);

  useEffect(() => { en().meta().then((r) => setMeta(r.data)).catch((e) => setErr(detailOf(e))); }, []);
  const load = async (kind, id) => {
    setErr(null); setResult(null); setConsent(false);
    try {
      const r = await en().prefill(kind, id);
      setForm(r.data); setFlow(r.data.flow || 'agent_assisted'); setPicking(false); setSendPlan(true);
      setGate(r.data.consent_gate || null); setWalkInConsent(null);
      setMode(r.data.consent_gate && r.data.consent_gate.state === 'cms_valid' ? 'cms' : 'hs_tick');
    } catch (e) { setErr(detailOf(e)); }
  };
  useEffect(() => { if (source) load(source.kind, source.id); }, [source?.kind, source?.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  // 5.40.0: re-read the gate (after a send, while waiting for the signature)
  const refreshGate = useCallback(async () => {
    if (!form) return;
    try {
      let g = null;
      if (form.person_id || form.client_id) {
        g = (await healthsherpa.consent.state({ person_id: form.person_id ?? null, client_id: form.person_id ? null : (form.client_id ?? null) })).data.gate;
      } else if (walkInConsent) {
        g = (await healthsherpa.consent.get(walkInConsent)).data.gate;
      }
      if (g) {
        setGate(g);
        if (g.state === 'cms_valid') setMode('cms');
        if (g.state !== 'cms_valid' && mode === 'cms') setMode('hs_tick');
      }
    } catch (_e) { /* keep the last state */ }
  }, [form, walkInConsent, mode]);
  const waiting = !!(gate && gate.pending && gate.state !== 'cms_valid' && !result);
  // 5.40.2 "Check now": asks Documenso about the waiting consent (POST /consent/{id}/check — the same idempotent
  // completion as the webhook / poll) and shows the fresh state; the 15-second refresh above stays database-only.
  const checkNow = async () => {
    const id = (gate && gate.pending && gate.pending.id) || walkInConsent;
    if (!id) { setCheckMsg({ ok: false, text: 'No consent is waiting.' }); return; }
    setChecking(true); setCheckMsg(null);
    try {
      const r = await healthsherpa.consent.check(id);
      setCheckMsg({ ok: r.data.result === 'signed', text: r.data.message || r.data.result });
      if (r.data.gate) {
        setGate(r.data.gate);
        if (r.data.gate.state === 'cms_valid') setMode('cms');
      }
    } catch (e) { setCheckMsg({ ok: false, text: detailOf(e) }); }
    setChecking(false);
  };
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(refreshGate, 15000);
    return () => clearInterval(t);
  }, [waiting, refreshGate]);
  const revoked = !!(gate && gate.state === 'revoked');
  const consentOk = !revoked && (mode === 'cms' ? !!(gate && gate.state === 'cms_valid') : consent);
  const primary = form ? (form.applicants.find((a) => a.relationship === 'primary') || {}) : {};

  const agent = flow === 'agent_assisted';
  const submit = async () => {
    setBusy(true); setErr(null); setHsErr(null); setResult(null);
    try {
      const r = await en().create(buildSessionBody(form, flow, consent, sendPlan, mode,
        mode === 'cms' && gate && gate.consent ? gate.consent.id : null));
      setResult({ ...r.data, template: (meta && meta.sms_template) || '' });
    } catch (e) {
      setErr(detailOf(e));
      setHsErr(e?.response?.data?.hs || null);
    }
    setBusy(false);
  };

  const hint = form && form.consent;
  return (
    <div role="dialog" aria-modal="true" aria-label="Start enrollment" data-testid="en-dialog"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 12px' }}>
      <div style={{ background: c.bg, color: c.text, borderRadius: 10, width: 'min(980px, 100%)', boxShadow: '0 10px 40px rgba(0,0,0,0.35)', padding: 18, display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: fs(17), fontWeight: 700, flex: 1 }}>Start enrollment <span style={{ fontWeight: 400, color: c.sub, fontSize: fs(13) }}>· HealthSherpa, on-exchange</span></div>
          <button ref={closeRef} style={s.btn(false)} onClick={onClose} disabled={busy}>Close</button>
        </div>
        <Err msg={err} />
        {hsErr && hsErr.status && (
          <div style={{ fontSize: fs(12), color: c.sub }} data-testid="en-hs-error">HealthSherpa answered HTTP {hsErr.status}{hsErr.code ? ` · ${hsErr.code}` : ''}</div>
        )}
        {picking && meta && <SourceChooser c={c} s={s} meta={meta} onPick={load} />}
        {form && !picking && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: fs(13) }}>
              <span>Prefilled from <strong>{sourceLabel(form)}</strong> — check every field.</span>
              {!result && <button style={{ ...s.btn(false), padding: '3px 10px' }} onClick={() => { setPicking(true); setForm(null); }}>Change</button>}
            </div>
            <div style={{ ...s.box, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div role="radiogroup" aria-label="Flow" style={{ display: 'flex', gap: 6 }}>
                {[['agent_assisted', 'Agent-assisted'], ['self_service', 'Self-service']].map(([k, l]) => (
                  <button key={k} role="radio" aria-checked={flow === k} disabled={!!result} style={s.btn(flow === k, !!result)} onClick={() => setFlow(k)} data-testid={`en-flow-${k}`}>{l}</button>
                ))}
              </div>
              <div><label style={s.label} htmlFor="en-year">Plan year</label>
                <select id="en-year" style={s.input(100)} value={form.plan_year} disabled={!!result} onChange={(e) => setForm((f) => ({ ...f, plan_year: Number(e.target.value) }))}>
                  {Array.from(new Set([...(meta?.plan_years || []), form.plan_year])).sort().map((y) => <option key={y} value={y}>{y}</option>)}
                </select></div>
              <div><label style={s.label} htmlFor="en-locale">Language</label>
                <select id="en-locale" style={s.input(110)} value={form.locale} disabled={!!result} onChange={(e) => setForm((f) => ({ ...f, locale: e.target.value }))}>
                  <option value="en-US">English</option><option value="es-MX">Spanish</option>
                </select></div>
            </div>
            <fieldset disabled={!!result} style={{ border: 'none', margin: 0, padding: 0, display: 'grid', gap: 12 }}>
              <div style={s.box}>
                <div style={s.h}>Where</div>
                <CountyBox c={c} s={s} form={form} setForm={setForm} />
                {agent && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    <div style={{ flex: '2 1 220px' }}><label style={s.label} htmlFor="en-street">Street (agent-assisted only)</label>
                      <input id="en-street" style={s.input()} value={form.location.street} maxLength={50} onChange={(e) => setForm((f) => ({ ...f, location: { ...f.location, street: e.target.value } }))} /></div>
                    <div style={{ flex: '1 1 140px' }}><label style={s.label} htmlFor="en-city">City</label>
                      <input id="en-city" style={s.input()} value={form.location.city} maxLength={50} onChange={(e) => setForm((f) => ({ ...f, location: { ...f.location, city: e.target.value } }))} /></div>
                  </div>
                )}
              </div>
              <div style={s.box}>
                <div style={s.h}>Household</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                  <div><label style={s.label} htmlFor="en-income">Annual household income ($)</label>
                    <input id="en-income" style={s.input(160)} value={form.household.annual_income} onChange={(e) => setForm((f) => ({ ...f, household: { ...f.household, annual_income: e.target.value.replace(/[^\d.]/g, ''), touched: Array.from(new Set([...(f.household.touched || []), 'annual_income'])), carried: (f.household.carried || []).filter((k) => k !== 'annual_income') } }))} />
                    {(form.household.carried || []).includes('annual_income') && <div style={{ fontSize: fs(11), color: '#b45309' }} data-testid="en-income-last">Last year's income — check it with the client.</div>}
                    <div style={{ fontSize: fs(11), color: c.sub, marginTop: 3, maxWidth: 260 }} data-testid="en-income-hint">Income gives the client exact prices — leave blank if unknown.</div></div>
                  <div><label style={s.label} htmlFor="en-size">Household size</label>
                    <input id="en-size" style={s.input(80)} value={form.household.household_size} onChange={(e) => setForm((f) => ({ ...f, household: { ...f.household, household_size: e.target.value.replace(/\D/g, '').slice(0, 2), touched: Array.from(new Set([...(f.household.touched || []), 'household_size'])), carried: (f.household.carried || []).filter((k) => k !== 'household_size') } }))} />
                    {(form.household.carried || []).includes('household_size') && <div style={{ fontSize: fs(11), color: '#b45309' }} data-testid="en-size-last">Last year's size</div>}</div>
                </div>
                <PeopleTable c={c} s={s} form={form} setForm={setForm} agent={agent} />
              </div>
              {form.plan_id && (
                <div style={{ ...s.box, fontSize: fs(13) }} data-testid="en-plan">
                  {agent ? (
                    <span>Plan from the quote: <strong>{form.plan_label}</strong> — <em>not sent</em> (agent-assisted never sends a plan; pick it with the client in HealthSherpa).</span>
                  ) : (
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="checkbox" checked={sendPlan} onChange={(e) => setSendPlan(e.target.checked)} />
                      Send the quote’s plan: <strong>{form.plan_label}</strong>
                    </label>
                  )}
                </div>
              )}
            </fieldset>
            <div style={{ ...s.box, borderColor: consentOk ? '#2e7d32' : revoked ? '#A32D2D' : '#b9770e' }} data-testid="en-consent">
              <div style={s.h}>Consent</div>
              {revoked ? (
                <div data-testid="en-consent-revoked" style={{ fontSize: fs(14), fontWeight: 700, color: STATE_TONE.revoked, marginBottom: 6 }}>{gateWords(gate)}</div>
              ) : (
                <>
                  {gate && gate.state === 'cms_valid' && (
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: fs(14), fontWeight: 600, marginBottom: 6 }}>
                      <input type="radio" name="en-consent-mode" checked={mode === 'cms'} onChange={() => setMode('cms')} data-testid="en-consent-cms" />
                      <span style={{ color: STATE_TONE.cms_valid }}>{gateWords(gate)}</span>
                    </label>
                  )}
                  <div style={{ fontSize: fs(12), color: c.sub, marginBottom: 6 }} data-testid="en-consent-hint">
                    {hint ? <>HealthSherpa export: <strong>{hint.value || 'no consent status'}</strong>{hint.export_label ? ` (from export of ${hint.export_label})` : ''}</> : 'No HealthSherpa export data for this person — check consent in HealthSherpa.'}
                  </div>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: fs(14), fontWeight: 600 }}>
                    {gate && gate.state === 'cms_valid' && <input type="radio" name="en-consent-mode" checked={mode === 'hs_tick'} onChange={() => setMode('hs_tick')} data-testid="en-consent-hsmode" />}
                    <input type="checkbox" checked={consent} onChange={(e) => { setConsent(e.target.checked); if (e.target.checked) setMode('hs_tick'); }} data-testid="en-consent-tick" />
                    {(meta && meta.consent_label) || 'Consent completed in HealthSherpa — I checked'}
                  </label>
                </>
              )}
              {gate && gate.state !== 'cms_valid' && !revoked && gate.state !== 'none' && (
                <div style={{ fontSize: fs(12), color: STATE_TONE[gate.state] || c.sub, marginTop: 6 }} data-testid="en-consent-state">{gateWords(gate)}</div>
              )}
              {waiting && (
                <div style={{ fontSize: fs(12), color: STATE_TONE.pending, marginTop: 6 }} data-testid="en-consent-waiting">
                  {gateWords({ ...gate, state: 'none' })} · <button onClick={checkNow} disabled={checking} data-testid="en-consent-check" style={{ background: 'none', border: 'none', color: '#4a8fd1', cursor: 'pointer', padding: 0, fontSize: fs(12) }}>{checking ? 'Checking…' : 'Check now'}</button>
                </div>
              )}
              {checkMsg && (
                <div role="status" data-testid="en-consent-check-result" style={{ fontSize: fs(12), fontWeight: 600, marginTop: 4, color: checkMsg.ok ? STATE_TONE.cms_valid : '#c0392b' }}>
                  {checkMsg.ok ? '✓ ' : ''}{checkMsg.text}
                </div>
              )}
              {!result && gate && gate.state !== 'cms_valid' && (
                <div style={{ marginTop: 8 }}>
                  <button style={s.btn(revoked)} onClick={() => setSendConsent(true)} data-testid="en-consent-send">
                    {gate.pending ? 'Resend CMS consent form' : 'Send CMS consent form'}
                  </button>
                  <span style={{ fontSize: fs(11), color: c.sub, marginLeft: 8 }}>The client signs by text; once signed it counts as consent for 5 years.</span>
                </div>
              )}
            </div>
            {sendConsent && (
              <ConsentSendDialog darkMode={darkMode} target={{ person_id: form.person_id ?? null, client_id: form.person_id ? null : (form.client_id ?? null) }}
                initial={{ consumer_name: [primary.first_name, primary.last_name].filter(Boolean).join(' '), phone: primary.phone || '', email: primary.email || '' }}
                onSent={(d) => { if (!form.person_id && !form.client_id) setWalkInConsent(d.consent.id); if (d.gate) setGate(d.gate); }}
                onClose={() => { setSendConsent(false); refreshGate(); }} />
            )}
            {!result && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button style={s.btn(true, !consentOk || busy)} disabled={!consentOk || busy} onClick={submit} data-testid="en-submit">
                  {busy ? 'Asking HealthSherpa…' : agent ? 'Get agent-assisted link' : 'Get self-service link'}
                </button>
                <span style={{ fontSize: fs(12), color: c.sub }}>One request to HealthSherpa; nothing is retried automatically.</span>
              </div>
            )}
            {result && <Delivery c={c} s={s} result={result} form={form} consent={consentOk} onClose={onClose} />}
            {result && result.personal_info && <PiCaptureResult c={{ border: c.border, panel: c.panel, text: c.text, sub: c.sub }}
              result={result.personal_info} source="enrollment"
              valuesFor={(i) => { const a = form.applicants[i] || {}; const t2 = a.touched || []; const v = {};
                if (t2.includes('dob') && a.dob) v.dob = a.dob; if (t2.includes('sex') && a.sex) v.gender = a.sex;
                if (t2.includes('uses_tobacco') && a.uses_tobacco != null) v.tobacco = a.uses_tobacco; return v; }} />}
          </>
        )}
        {!form && !picking && !err && <div style={{ fontSize: fs(13), color: c.sub }}>Loading…</div>}
      </div>
    </div>
  );
}
